import type { ReactNode } from 'react';
import { clsx } from 'clsx';

const shellClass =
  'overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/[0.03] dark:border-dark-600 dark:bg-dark-800 dark:ring-white/[0.04]';

const headerClass =
  'border-b border-gray-200 bg-gradient-to-r from-gray-50 via-white to-slate-50/70 px-4 py-3 dark:border-dark-600 dark:from-dark-900/55 dark:via-dark-800 dark:to-dark-900/45 sm:px-5 sm:py-3.5';

type PoolReportTableCardProps = {
  /** Short tab label, e.g. "Contribution" — same pool-reports accent on every tab */
  eyebrow: string;
  title: string;
  description?: string;
  /** Export button, etc. — right side on larger screens */
  endAdornment?: ReactNode;
  /** Extra controls below the title row (filters, toggles) */
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * Shared chrome for pool report tab pages: compact header, hierarchy, consistent brand tint.
 */
export function PoolReportTableCard({
  eyebrow,
  title,
  description,
  endAdornment,
  toolbar,
  children,
  className,
}: PoolReportTableCardProps) {
  return (
    <div className={clsx(shellClass, className)}>
      <div className={headerClass}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#4a5682] dark:text-sky-300/90">
              {eyebrow}
            </p>
            <h2 className="type-section-title">{title}</h2>
            {description ? (
              <p className="mt-0.5 max-w-2xl text-xs leading-snug text-gray-500 dark:text-gray-400">
                {description}
              </p>
            ) : null}
          </div>
          {endAdornment ? (
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
              {endAdornment}
            </div>
          ) : null}
        </div>
        {toolbar ? (
          <div className="mt-3 flex flex-col gap-2 border-t border-gray-200/80 pt-3 dark:border-dark-600/80 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
            {toolbar}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
